import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { markAllNotificationsRead } from '../../utils/api/notificationApi';

const content = {
  ne: {
    title: 'सूचनाहरू',
    empty: 'कुनै सूचना छैन',
    markAll: 'सबै पढिए चिन्ह लगाउनुहोस्',
  },
  en: {
    title: 'Notifications',
    empty: 'No notifications',
    markAll: 'Mark all read',
  },
};

const TYPE_ROUTE_MAP = {
  news_submitted:        '/editor/articles',
  news_published:        '/editor/articles',
  news_rejected:         '/editor/articles',
};

function getNotificationLink(type) {
  return TYPE_ROUTE_MAP[type] || '/editor/dashboard';
}

const NotificationBell = ({ notifications = [], onMarkRead, onMarkAllRead, language }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const t = content[language] || content.en;
  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      onMarkAllRead?.();
    } catch (err) {
      console.error('Failed to mark all as read', err);
    }
  };

  const handleNotificationClick = (n) => {
    setOpen(false);
    if (!n.read) {
      onMarkRead?.(n._id);
    }
    navigate(getNotificationLink(n.type));
  };

  return (
    <div className="relative mr-2 mt-2" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-2xl border border-char-100 bg-white p-3 text-char-600 shadow-sm hover:bg-char-50"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-extrabold text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-72 rounded-2xl border border-char-100 bg-white p-2 shadow-xl">
          <div className="flex items-center justify-between px-3 py-2">
            <p className="text-xs font-extrabold uppercase tracking-wider text-char-400">
              {t.title}
            </p>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-600"
              >
                <CheckCheck size={13} />
                {t.markAll}
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-char-400">{t.empty}</p>
          ) : (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {notifications.map((n) => (
                <button
                  key={n._id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-char-50 ${
                    n.read ? 'text-char-400' : 'font-bold text-char-900'
                  }`}
                >
                  <p>{n.message}</p>
                  <p className="mt-0.5 text-xs text-char-400">
                    {new Date(n.createdAt).toLocaleTimeString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;